import _includesInstanceProperty from "@babel/runtime-corejs3/core-js/instance/includes";
import React from "react";
import { ExclusiveRange, useInclusiveRange } from "../Range/index.js";
import useThrotledAsyncCallback from "../useThrotledAsyncCallback/index.js";
import useAnimationTimeoutEffect from "./useAnimationTimeoutEffect.js";
import useReducerWithChangeCallback from "../useReducerWithChangeCallback/index.js";
import useActionCreators from "../useActionCreators/index.js";
import useRenderRef from "../useRenderRef/index.js";
const HIGHER_SENSITIVITY_DELTA_RANGE = new ExclusiveRange(20, 100);
const actionTypes = {
  DRAG: 'DRAG',
  MOVE: 'MOVE',
  ADJUST: 'ADJUST',
  RESET_ANIMATION: 'RESET_ANIMATION',
  CONTROLLED_NAVIGATION_INDEX_UPDATE: 'CONTROLLED_NAVIGATION_INDEX_UPDATE'
};
const animationStates = {
  DRAG: 'DRAG',
  MOVE: 'MOVE',
  ADJUST: 'ADJUST',
  NONE: null
};
const actionCreators = {
  drag: (offset, navigationIndexRange) => ({
    type: actionTypes.DRAG,
    offset,
    navigationIndexRange
  }),
  move: (offset, navigationIndexRange) => ({
    type: actionTypes.MOVE,
    offset,
    navigationIndexRange
  }),
  adjust: navigationIndexRange => ({
    type: actionTypes.ADJUST,
    navigationIndexRange
  }),
  resetAnimation: () => ({
    type: actionTypes.RESET_ANIMATION
  }),
  controlledNavigationIndexUpdate: nextNavigationIndex => ({
    type: actionTypes.CONTROLLED_NAVIGATION_INDEX_UPDATE,
    nextNavigationIndex
  })
};
const getInitialState = function () {
  let initialIndex = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : 0;
  return {
    animationState: animationStates.NONE,
    visualNavigationOffset: initialIndex,
    committedNavigationIndex: initialIndex
  };
};
const getNextIndexFromOffsetBoundToNavigationRange = (state, action) => action.navigationIndexRange.restrict(state.committedNavigationIndex + action.offset);
const reducer = (state, action) => {
  switch (action.type) {
    case actionTypes.CONTROLLED_NAVIGATION_INDEX_UPDATE:
      {
        let draggingState = {};
        if (state.animationState !== animationStates.DRAG) {
          draggingState = {
            visualNavigationOffset: action.nextNavigationIndex,
            animationState: animationStates.MOVE
          };
        }
        return {
          ...state,
          ...draggingState,
          committedNavigationIndex: action.nextNavigationIndex
        };
      }
    case actionTypes.RESET_ANIMATION:
      {
        return {
          ...state,
          animationState: animationStates.NONE
        };
      }
    case actionTypes.DRAG:
      {
        const nextIndex = getNextIndexFromOffsetBoundToNavigationRange(state, action);
        return {
          ...state,
          visualNavigationOffset: nextIndex,
          animationState: animationStates.DRAG
        };
      }
    case actionTypes.MOVE:
      {
        const nextIndex = getNextIndexFromOffsetBoundToNavigationRange(state, action);
        if (nextIndex === state.committedNavigationIndex) {
          return state;
        }
        return {
          ...state,
          visualNavigationOffset: nextIndex,
          committedNavigationIndex: nextIndex,
          animationState: animationStates.MOVE
        };
      }
    case actionTypes.ADJUST:
      {
        const delta = state.visualNavigationOffset - state.committedNavigationIndex;
        const absDelta = Math.abs(delta);
        let newNavigationIndex;
        if (_includesInstanceProperty(HIGHER_SENSITIVITY_DELTA_RANGE).call(HIGHER_SENSITIVITY_DELTA_RANGE, absDelta * 100)) {
          newNavigationIndex = delta > 0 ? Math.ceil(state.visualNavigationOffset) : Math.floor(state.visualNavigationOffset);
        } else {
          newNavigationIndex = Math.round(state.visualNavigationOffset);
        }
        const finalNavigationIndex = action.navigationIndexRange.restrict(newNavigationIndex);
        return {
          ...state,
          visualNavigationOffset: finalNavigationIndex,
          committedNavigationIndex: finalNavigationIndex,
          animationState: animationStates.ADJUST
        };
      }
    default:
      {
        return state;
      }
  }
};
const useNavigation = (props, _ref) => {
  let {
    totalSlidesCount,
    visibleSlidesCount
  } = _ref;
  const isControlled = React.useRef(typeof props.navigationIndex !== 'undefined');
  const initialState = isControlled.current ? getInitialState(props.navigationIndex) : getInitialState(props.navigationIndexDefaultValue);

  /**
   * Use a ref to call the onNavigationChange callback in `useEffect`. This is helpful to ensure the
   * effect always has the latest version of the callback, without triggering the effect every time
   * the callback changes.
   */
  const onNavigationIndexChangeRef = useRenderRef(props.onNavigationIndexChange);
  const onNavigationRangeChangeRef = useRenderRef(props.onNavigationRangeChange);
  const onStateChange = React.useCallback((currentState, nextState, action) => {
    if (action.type === actionTypes.CONTROLLED_NAVIGATION_INDEX_UPDATE) {
      return;
    }
    if (nextState.committedNavigationIndex !== currentState.committedNavigationIndex) {
      onNavigationIndexChangeRef.current(nextState.committedNavigationIndex);
    }
  }, []);
  const [state, dispatch] = useReducerWithChangeCallback(reducer, initialState, onStateChange);
  const actions = useActionCreators(actionCreators, dispatch);
  const visibleSlidesIndexRange = useInclusiveRange(state.committedNavigationIndex, state.committedNavigationIndex + Math.max(visibleSlidesCount - 1, 0));
  const pageSize = props.pagination === 'auto' ? visibleSlidesIndexRange.length : props.pagination;
  const navigationIndexRange = useInclusiveRange(0, totalSlidesCount - visibleSlidesCount);
  const adjust = React.useCallback(() => {
    actions.adjust(navigationIndexRange);
  }, [navigationIndexRange, actions.adjust]);
  const resetAnimation = useThrotledAsyncCallback(() => {
    actions.resetAnimation();
  }, [dispatch]);

  /**
   * Side-effects
   */

  useAnimationTimeoutEffect(state.animationState, resetAnimation);
  const adjustRef = useRenderRef(adjust);
  React.useEffect(() => {
    onNavigationRangeChangeRef.current(navigationIndexRange);
    adjustRef.current();
  }, [navigationIndexRange]);
  if (state.committedNavigationIndex !== props.navigationIndex && isControlled.current) {
    actions.controlledNavigationIndexUpdate(props.navigationIndex);
  }
  return React.useMemo(() => {
    const move = offset => {
      actions.move(offset, navigationIndexRange);
    };
    const moveLeft = () => {
      move(-pageSize);
    };
    const moveRight = () => {
      move(pageSize);
    };
    const drag = offset => {
      actions.drag(offset, navigationIndexRange);
    };
    const canMove = offset => {
      const nextIndex = state.committedNavigationIndex + offset;
      return _includesInstanceProperty(navigationIndexRange).call(navigationIndexRange, nextIndex);
    };
    return {
      // actions
      move,
      moveLeft,
      moveRight,
      drag,
      adjust,
      resetAnimation,
      // state
      visualNavigationOffset: state.visualNavigationOffset,
      committedNavigationIndex: state.committedNavigationIndex,
      animationState: state.animationState,
      visibleSlidesIndexRange,
      navigationIndexRange,
      // selectors
      canMove
    };
  }, [actions, state, adjust, resetAnimation, pageSize, navigationIndexRange, visibleSlidesIndexRange]);
};
export default useNavigation;